import { Head, router } from '@inertiajs/react';
import { useState, useMemo } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import PageHeader from '@/Components/PageHeader';
import FormInput from '@/Components/FormInput';
import FormSelect from '@/Components/FormSelect';
import { ArrowDownTrayIcon, UsersIcon, QrCodeIcon, CalendarIcon, FunnelIcon, DocumentTextIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

function StatCard({ icon: Icon, label, value, color = 'mint' }) {
    const colorMap = { mint: 'bg-emerald-100 text-emerald-800', blue: 'bg-blue-100 text-blue-600', purple: 'bg-purple-100 text-purple-600', amber: 'bg-amber-100 text-amber-600' };
    return (
        <Card>
            <CardBody className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${colorMap[color]} flex items-center justify-center flex-shrink-0`}><Icon className="w-6 h-6" /></div>
                <div><p className="text-sm text-slate-500">{label}</p><p className="text-2xl font-bold text-slate-800">{value?.toLocaleString?.() || value || 0}</p></div>
            </CardBody>
        </Card>
    );
}

export default function Index({ visitors = [], exhibitions = [], flash }) {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [type, setType] = useState('');
    const [exhibitionId, setExhibitionId] = useState('');

    const stats = useMemo(() => {
        const total = visitors.length;
        const suppliers = visitors.filter(v => v.type === 'Supplier').length;
        const customers = visitors.filter(v => v.type === 'Customer').length;
        const others = total - suppliers - customers;
        return { total, suppliers, customers, others };
    }, [visitors]);

    const applyFilters = (e) => {
        e?.preventDefault();
        router.get('/reports', {
            start_date: startDate,
            end_date: endDate,
            type: type || undefined,
            exhibition_id: exhibitionId || undefined,
        }, { preserveState: true });
    };

    const exportParams = new URLSearchParams({
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
        ...(type && { type }),
        ...(exhibitionId && { exhibition_id: exhibitionId }),
    }).toString();

    return (
        <AppLayout>
            <Head title="Reports" />
            <PageHeader title="Reports & Analytics" subtitle="View and export data reports" actions={
                <div className="flex gap-2">
                    <a href={`/reports/export-csv${exportParams ? '?' + exportParams : ''}`} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg transition-colors"><ArrowDownTrayIcon className="w-4 h-4" /> CSV</a>
                    <a href={`/reports/export-pdf${exportParams ? '?' + exportParams : ''}`} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-800 text-white rounded-lg transition-colors"><DocumentTextIcon className="w-4 h-4" /> PDF</a>
                </div>
            } />

            {/* Filters */}
            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="flex flex-col sm:flex-row gap-3 items-end">
                        <FormInput label="From" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full sm:w-48" />
                        <FormInput label="To" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full sm:w-48" />
                        <FormSelect label="Type" value={type} onChange={e => setType(e.target.value)} className="w-full sm:w-48">
                            <option value="">All</option>
                            <option value="Customer">Customer</option>
                            <option value="Supplier">Supplier</option>
                        </FormSelect>
                        {exhibitions.length > 0 && (
                            <FormSelect label="Exhibition" value={exhibitionId} onChange={e => setExhibitionId(e.target.value)} className="w-full sm:w-48">
                                <option value="">All Exhibitions</option>
                                {exhibitions.map(ex => (
                                    <option key={ex.id} value={ex.id}>{ex.name}</option>
                                ))}
                            </FormSelect>
                        )}
                        <Button type="submit" size="md"><FunnelIcon className="w-4 h-4" /> Apply</Button>
                    </form>
                </CardBody>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard icon={UsersIcon} label="Total Records" value={stats.total} color="mint" />
                <StatCard icon={BuildingOfficeIcon} label="Suppliers" value={stats.suppliers} color="blue" />
                <StatCard icon={QrCodeIcon} label="Customers" value={stats.customers} color="purple" />
                <StatCard icon={CalendarIcon} label="Others" value={stats.others} color="amber" />
            </div>

            {/* Data Table */}
            {visitors.length > 0 && (
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-800">Report Data</h3></div>
                    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                        <table className="w-full text-sm" style={{ minWidth: '700px' }}>
                            <thead><tr className="bg-slate-50 border-b border-slate-200">
                                <th className="text-left px-4 py-3 font-semibold text-slate-600">Name</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-600">Company</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-600">Email</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-600">Type</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-600">Exhibition</th>
                                <th className="text-left px-4 py-3 font-semibold text-slate-600">Date</th>
                            </tr></thead>
                            <tbody>
                                {visitors.map((v, i) => (
                                    <tr key={v.id || i} className="border-b border-slate-100 hover:bg-slate-50/50">
                                        <td className="px-4 py-3 font-medium text-slate-800">{v.personal_contact_name || v.name || 'N/A'}</td>
                                        <td className="px-4 py-3 text-slate-600">{v.company_name || 'N/A'}</td>
                                        <td className="px-4 py-3 text-slate-500">{v.email || 'N/A'}</td>
                                        <td className="px-4 py-3"><Badge variant="neutral">{v.type || 'Visitor'}</Badge></td>
                                        <td className="px-4 py-3 text-slate-500">{v.exhibition?.name || 'N/A'}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{v.created_at ? new Date(v.created_at).toLocaleDateString() : 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {visitors.length === 0 && (
                <Card>
                    <CardBody className="text-center py-12">
                        <UsersIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No records found. Try adjusting your filters.</p>
                    </CardBody>
                </Card>
            )}
        </AppLayout>
    );
}
